/**
 * Server-Sent Events framing.
 *
 * Shared because getting this subtly wrong fails *silently*: the stream is
 * consumed, no frame is ever emitted, and the caller sees an empty response
 * with a 200 status and no error anywhere.
 *
 * Two things bite:
 *  1. Google's streaming endpoint separates frames with CRLF (`\r\n\r\n`) and
 *     emits no bare `\n\n` at all, so `split('\n\n')` matches nothing.
 *  2. The final frame usually arrives without a trailing blank line, so a
 *     parser that only emits on a separator drops the last chunk.
 *
 * The SSE spec permits CRLF, LF or CR as line terminators, so handle all three.
 */

export interface SseSplit {
  /** Complete frames' `data:` payloads, in order. */
  events: string[];
  /** The trailing partial frame, to be prepended to the next read. */
  rest: string;
}

/**
 * Split an SSE buffer into complete frames.
 *
 * @param flush when true, treat the trailing partial frame as complete. Call
 *              this once after the stream closes.
 */
export function splitSseFrames(buffer: string, flush = false): SseSplit {
  const blocks = buffer.split(/\r\n\r\n|\n\n|\r\r/);
  // Without `flush`, the trailing element is an incomplete frame — hold it back.
  const rest = flush ? '' : (blocks.pop() ?? '');

  const events: string[] = [];
  for (const block of blocks) {
    if (!block.trim()) continue;
    // A frame may carry several `data:` lines; the spec joins them with \n.
    const data = block
      .split(/\r\n|\n|\r/)
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).replace(/^ /, ''))
      .join('\n');
    if (data.trim()) events.push(data.trim());
  }
  return { events, rest };
}

/** Convenience wrapper: turn a byte stream into decoded SSE payload strings. */
export async function* readSseStream(
  body: ReadableStream<Uint8Array>,
  signal?: AbortSignal,
): AsyncGenerator<string> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      buffer += decoder.decode(value, { stream: true });
      const { events, rest } = splitSseFrames(buffer);
      buffer = rest;
      for (const event of events) yield event;
    }
    buffer += decoder.decode();
    for (const event of splitSseFrames(buffer, true).events) yield event;
  } finally {
    reader.releaseLock();
  }
}
